import React, { useEffect, useState } from "react";
import Layout from "../shared/Layout";
import Select from "react-select";
import { useQuery } from "react-query";
import { supabase } from "../../supabaseClient";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../authentication/AuthContext";
import { getGeminiCompletion } from "../ai/GeminiUtils";
import { Guide } from "./models/Guide";
import { useShowTempErrorMessage } from "../shared/hooks/useUtilityContext";
import { Task } from "./models/Task";

export default function CreateGuide() {
  const [title, setTitle] = useState(localStorage.getItem("guide_title") ?? "");
  const [steps, setSteps] = useState<Task[]>([]);
  const [searchTitle, setSearchTitle] = useState<string>();
  const [prompt, setPrompt] = useState<string>(
    localStorage.getItem("guide_prompt") ?? ""
  );
  const [guide, setGuide] = useState<string>();
  const [selectedOption, setSelectedOption] = useState<{
    value: string;
    label: string;
  } | null>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [creatingGuide, setCreatingGuide] = useState(false);
  const showTempErrorMessage = useShowTempErrorMessage();

  useEffect(() => {
    localStorage.setItem("guide_title", title);
  }, [title]);

  useEffect(() => {
    localStorage.setItem("guide_prompt", prompt);
  }, [prompt]);

  const {
    data: searchSteps,
    isLoading,
    isError,
  } = useQuery(["search-guides", searchTitle], async () => {
    return supabase
      .from("tasks")
      .select("*")
      .ilike("title", `%${searchTitle}%`)
      .limit(20)
      .order("created_at", { ascending: false });
  });

  return (
    <Layout>
      <div className="text-md font-bold mt-8">Guide Title</div>
      <div className="prose text-sm font-light">
        What does this guide accomplish?
      </div>
      <input
        placeholder="Title"
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="input input-bordered w-full mt-2"
      ></input>
      <div className="text-md font-bold mt-8">Guide Prompt (optional)</div>
      <div className="prose text-sm font-light">
        We use an AI model to convert the steps into a guide. This prompt will
        be used in that process.
      </div>
      <input
        placeholder="Prompt"
        type="text"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        className="input input-bordered w-full mt-2"
      ></input>
      <div className="text-md font-bold mt-8">Steps</div>
      <div className="prose text-sm font-light">
        Add at least 2 steps to the guide.
      </div>
      <div className="flex items-center gap-4 mt-2">
        <Select
          placeholder="Search for a step"
          className="w-full cursor-pointer"
          /* styles={{
            control: (base) => ({
              ...base,
              height: 40,
              minHeight: 40,
              borderRadius: 0,
              background: "bg-",
            }),
            option: (base) => ({
                ...base,
                padding: "8px 12px",
                background: "#3066BE",
                }),
          }} */
          classNames={{
            container: (state) => "!cursor-pointer",
            valueContainer: (state) => "!cursor-pointer",
            placeholder: (state) => "!text-base-content",
            singleValue: (state) => "!text-base-content",
            input: (state) => "!text-base-content",
            option: (state) =>
              "!text-base-content !bg-base-200 hover:!bg-base-300 !cursor-pointer hover:!text-primary",
            control: (state) => "!bg-inherit !text-inherit",
            menu: (state) => "!text-base-content !bg-base-200",
          }}
          value={selectedOption}
          onChange={(e) =>
            setSelectedOption({
              value: e?.value ?? "",
              label: e?.label ?? "",
            })
          }
          options={searchSteps?.data?.map((step) => ({
            value: step.id,
            label: step.title,
          }))}
          inputValue={searchTitle}
          onInputChange={(e) => setSearchTitle(e)}
          onMenuClose={() => {}}
          onMenuOpen={() => {}}
        />
        <button
          className="btn btn-ghost"
          onClick={() => {
            const step = searchSteps?.data?.find(
              (step) => step.id === selectedOption?.value
            );

            if (step) {
              setSteps([...steps, step]);
              setSelectedOption(null);
              setSearchTitle("");
            }
          }}
        >
          + Add Step
        </button>
      </div>
      {steps.map((step, index) => (
        <div
          key={index}
          className="mt-4"
        >
          <div className="text-md font-bold">Step {index + 1}</div>
          <div className="prose text-sm font-semibold">{step.title}</div>
          <div className="prose text-sm font-light line-clamp-3">
            {step.description}
          </div>
        </div>
      ))}
      <button
        title="Add Step"
        onClick={async () => {
          try {
            setCreatingGuide(true);
            const guide = await getGeminiCompletion({
              prompt: `Create a guide titled ${title}.

            ${prompt}
            
            Use the following steps: ${steps
              .map((step) => step.title + ": " + step.description)
              .join("\n\n")}`,
              model: "gemini-1.5-pro-latest",
            });

            setGuide(guide);
            console.log("guide", guide);

            const newGuide: Guide = {
              title,
              prompt,
              created_at: new Date().toISOString(),
              user_id: user!.id,
              steps: steps.map((step) => step.id!),
              guide,
              updated_at: new Date().toISOString(),
              updated_by: user!.id,
            };

            const { data: guideId, error } = await supabase
              .from("guides")
              .insert(newGuide)
              .select("id");
            setCreatingGuide(false);

            if (error) {
              showTempErrorMessage(`Error creating guide. ${error.message}`);
            } else {
              navigate(`/guide/${guideId![0].id}`);
            }
          } catch (e) {
            console.error(e);
            setCreatingGuide(false);
          }
        }}
        disabled={creatingGuide || !title || steps.length < 2}
        className="btn btn-primary w-full mt-8"
      >
        {creatingGuide && <div className="loading loading-dots mr-4"></div>}
        Create Guide
      </button>
      {/* {guide && (
        <div className="my-16">
          <MarkdownRenderer content={guide} />
        </div>
      )} */}
    </Layout>
  );
}
